import { ContentShell } from "@/pages/ContentPages";

const LAST_UPDATED = "3 February 2026";
const ENTITY = "Kaada Nordic MarTech";
const ORG_NR = "936 289 835";
const CONTACT_EMAIL = "hello@nordicmartech.com";
const DPO_EMAIL = "privacy@nordicmartech.com";
const ADDRESS = "Oslo, Norway";

/* ─────────────── Privacy ─────────────── */

export function Privacy() {
  return (
    <ContentShell
      eyebrow="Privacy"
      h1="Privacy policy"
      intro={`How ${ENTITY} (Org nr. ${ORG_NR}) collects, uses and protects information when you use PressImpact. Last updated ${LAST_UPDATED}.`}
      cta={{ to: "/calculator", label: "Open the calculator" }}
      sections={[
        {
          h2: "Who is the data controller",
          body: [
            `The data controller for PressImpact is ${ENTITY}.`,
            `Organisasjonsnummer: ${ORG_NR} · Registered in ${ADDRESS}.`,
            ["callout", `For any privacy question, email ${DPO_EMAIL}. We respond within 30 days.`],
          ],
        },
        {
          h2: "What personal data we collect",
          body: [
            "PressImpact is designed to work with the minimum viable personal data. We collect only what is necessary to deliver the service:",
            ["list", [
              "Email address you enter to receive a report",
              "Campaign inputs you type into the calculator",
              "Language and currency preferences you set",
              "Standard technical metadata (IP, browser user-agent) that arrives with every request",
            ]],
            "We do not use tracking pixels, third-party analytics cookies, or advertising trackers.",
            ["callout", "A single first-party cookie remembers your language and currency on this device. That's it."],
          ],
        },
        {
          h2: "How we use the data",
          body: [
            "Campaign inputs are used to compute your ROI report and generate the executive summary.",
            "Your email is used only to deliver the report you requested and — if you opt in — to send occasional product updates.",
            "Technical metadata keeps the service secure and helps debug errors.",
            ["quote", "We do not sell, rent, or share your data with advertisers."],
          ],
        },
        {
          h2: "Legal basis (GDPR)",
          body: [
            ["list", [
              "Contract (Art. 6(1)(b)) — delivering the service you requested.",
              "Legitimate interest (Art. 6(1)(f)) — keeping the service secure and improving accuracy.",
              "Consent (Art. 6(1)(a)) — optional product-update emails, withdrawable via the unsubscribe link in every message.",
            ]],
          ],
        },
        {
          h2: "Retention",
          body: [
            "Reports and their inputs are retained for up to 90 days from creation, then automatically expire.",
            "After expiry the shareable URL returns a 410 response.",
            ["list", [
              "Emails used for a single report: purged 30 days after that report expires.",
              "Log data: rotated after 30 days.",
              "Product-update subscribers: retained until you unsubscribe.",
            ]],
          ],
        },
        {
          h2: "Sub-processors",
          body: [
            "A small set of EU-hosted sub-processors runs the service:",
            ["list", [
              "MongoDB Atlas (Frankfurt) — report storage",
              "Brevo (Paris) — transactional email",
              "European Central Bank public API — currency reference rates",
              "Anthropic — AI narrative engine (US, under Standard Contractual Clauses)",
              "Stripe — payments",
            ]],
            "Data processing agreements are in place where required by GDPR.",
          ],
        },
        {
          h2: "Your rights",
          body: [
            "Under GDPR you have the right to:",
            ["list", [
              "Access the personal data we hold about you",
              "Correct or update it",
              "Have it deleted",
              "Restrict or object to processing",
              "Withdraw any consent you have given",
              "Lodge a complaint with a supervisory authority",
            ]],
            `To exercise any of these, email ${DPO_EMAIL} with the email address associated with your reports.`,
            ["callout", "In Norway, the relevant supervisory authority is Datatilsynet (datatilsynet.no)."],
          ],
        },
        {
          h2: "International transfers",
          body: [
            "Primary storage and processing happens inside the EU/EEA.",
            "Some sub-processors (notably the AI narrative engine) process data in the United States under Standard Contractual Clauses approved by the European Commission.",
            "We minimise the data sent to those processors — the AI narrative engine only receives your computed numbers, not your email address.",
          ],
        },
        {
          h2: "Changes to this policy",
          body: [
            "We update this page if we materially change how personal data is handled.",
            "The latest version is always available at this URL.",
            "Material changes: subscribed users are notified by email at least 14 days in advance.",
          ],
        },
      ]}
    />
  );
}

/* ─────────────── Terms ─────────────── */

export function Terms() {
  return (
    <ContentShell
      eyebrow="Terms"
      h1="Terms of service"
      intro={`These terms govern your use of PressImpact, provided by ${ENTITY} (Org nr. ${ORG_NR}). By using PressImpact you agree to them. Last updated ${LAST_UPDATED}.`}
      cta={{ to: "/calculator", label: "Open the calculator" }}
      sections={[
        {
          h2: "The service",
          body: [
            "PressImpact is a decision-support tool that estimates the potential business impact of a press release using your inputs and public industry benchmarks.",
            ["quote", "Every number is a projection. Not verified revenue. Not a guarantee."],
            "You are responsible for the inputs you provide and the decisions you make with the output.",
          ],
        },
        {
          h2: "Free and paid tiers",
          body: [
            "PressImpact offers a genuinely free tier (no credit card, no time limit) and paid subscription tiers.",
            ["list", [
              "Subscriptions renew automatically until cancelled.",
              "Cancel any time via the Stripe customer portal.",
              "Cancellation takes effect at the end of the current billing period.",
              "Prices are canonical in EUR — payment currency is confirmed at checkout.",
            ]],
          ],
        },
        {
          h2: "Acceptable use",
          body: [
            "You may not use PressImpact to:",
            ["list", [
              "Reverse-engineer or copy the service",
              "Submit content that is unlawful or infringes third-party rights",
              "Attempt to bypass rate limits or free-tier caps",
              "Resell access without a licensed Agency tier",
            ]],
            "We may suspend accounts that materially breach these terms after giving reasonable notice.",
          ],
        },
        {
          h2: "Intellectual property",
          body: [
            `PressImpact, the brand, the calculator engine, benchmark seeds and modelled defaults are the intellectual property of ${ENTITY}.`,
            ["callout", "You retain full ownership of the campaign inputs you enter. Reports are yours to use internally and share externally under a read-only link."],
          ],
        },
        {
          h2: "Warranties and liability",
          body: [
            "The service is provided on an \"as is\" basis.",
            "We make reasonable efforts to keep benchmarks current and formulas accurate — but no warranty is given that the estimate will match your actual outcome.",
            "To the maximum extent permitted by Norwegian law, our aggregate liability is limited to the fees you paid in the 12 months before the event giving rise to the claim.",
            ["callout", "Nothing in these terms limits liability for fraud, gross negligence, or death or personal injury caused by negligence."],
          ],
        },
        {
          h2: "Termination",
          body: [
            "You can stop using PressImpact at any time.",
            "We may terminate or suspend access if you materially breach these terms.",
            "If we discontinue the service, we'll provide 60 days' notice and offer a data export.",
          ],
        },
        {
          h2: "Governing law",
          body: [
            "These terms are governed by Norwegian law.",
            "Any dispute that cannot be resolved amicably is submitted to the exclusive jurisdiction of the courts of Oslo, Norway.",
          ],
        },
        {
          h2: "Contact",
          body: [
            ["callout", `Questions about these terms? Email ${CONTACT_EMAIL}.`],
          ],
        },
      ]}
    />
  );
}

/* ─────────────── Imprint / Legal notice ─────────────── */

export function Imprint() {
  return (
    <ContentShell
      eyebrow="Imprint"
      h1="Legal notice"
      intro="Information required under Norwegian and EU disclosure rules."
      cta={{ to: "/", label: "Back to home" }}
      sections={[
        {
          h2: "Company",
          body: [
            `${ENTITY} — a limited company registered in Norway.`,
            ["list", [
              `Organisasjonsnummer: ${ORG_NR}`,
              `Registered office: ${ADDRESS}`,
              `Founded to make PR ROI defensible for Nordic teams`,
            ]],
          ],
        },
        {
          h2: "Contact",
          body: [
            ["list", [
              `General enquiries: ${CONTACT_EMAIL}`,
              `Privacy and data protection: ${DPO_EMAIL}`,
              `Sales: sales@nordicmartech.com`,
            ]],
          ],
        },
        {
          h2: "Responsible for content",
          body: [
            `${ENTITY}, ${ADDRESS}, is responsible for the content of this website and the PressImpact service.`,
          ],
        },
        {
          h2: "VAT",
          body: [
            "VAT registration and treatment are shown on invoices issued via Stripe.",
            ["callout", `Need a copy of a specific invoice? Email ${CONTACT_EMAIL}.`],
          ],
        },
        {
          h2: "Consumer dispute resolution",
          body: [
            "We do not participate in dispute-resolution proceedings before a consumer arbitration board.",
            "The European Commission provides a platform for online dispute resolution at ec.europa.eu/consumers/odr.",
          ],
        },
        {
          h2: "Copyright",
          body: [
            `© ${new Date().getFullYear()} ${ENTITY}. All rights reserved.`,
            "All content on this site — copy, benchmark seed data, illustrations, and the PressImpact brand mark — is the property of the entity above unless otherwise stated.",
            ["callout", "Reproduction requires written permission."],
          ],
        },
      ]}
    />
  );
}
